import React, { useState } from "react";
import { Link } from "react-router-dom";
import { CheckCircle2 } from "lucide-react";
import { api } from "@/lib/api";

/**
 * Post-result CTA — shown after a free tool produces a result.
 *
 * Implements:
 *   - Zeigarnik Effect: open-loop question that creates cognitive pull
 *   - Commitment & Consistency: inline email capture (small commitment)
 *   - Social Proof: optional testimonial quote at the point of highest intent
 *
 * Props:
 *   openLoop    string    — The hook question / tension statement
 *   source      string    — Brevo TOOL_SOURCE attribute (e.g. "policy-checker")
 *   nextPath    string    — Path for the post-submit cross-link (keeps loop open)
 *   nextLabel   string    — Label for the cross-link
 *   testimonial object|null — { quote, name, role } — omit to suppress
 */
export default function PostResultCTA({ openLoop, source, nextPath, nextLabel, testimonial = null }) {
  const [email, setEmail] = useState("");
  const [loading, setLoading] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [error, setError] = useState(null);

  async function handleSubmit(e) {
    e.preventDefault();
    if (!email) return;
    setLoading(true);
    setError(null);
    try {
      await api.post("/waitlist", { email, ...(source ? { source } : {}) });
      setSubmitted(true);
    } catch (err) {
      setError(err?.response?.data?.detail || "Something went wrong. Please try again.");
    } finally {
      setLoading(false);
    }
  }

  return (
    <div className="border border-brand/30 rounded-lg bg-brand/5 overflow-hidden">
      {/* Open loop + email capture */}
      <div className="p-5">
        <p className="font-medium text-sm text-brand mb-4 leading-relaxed">{openLoop}</p>

        {submitted ? (
          <div className="space-y-3">
            <div className="flex items-center gap-2 text-sm">
              <CheckCircle2 className="h-4 w-4 text-brand shrink-0" />
              <span>You&apos;re on the list. We&apos;ll be in touch.</span>
            </div>
            {nextPath && (
              <Link
                to={nextPath}
                className="inline-flex items-center gap-1 text-sm text-brand hover:opacity-80 transition-opacity font-medium"
              >
                {nextLabel || "Next →"}
              </Link>
            )}
          </div>
        ) : (
          <form onSubmit={handleSubmit} className="flex gap-2">
            <input
              type="email"
              required
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              placeholder="your@company.com"
              className="flex-1 rounded-md border border-border bg-background px-3 py-2 text-sm placeholder:text-muted-foreground focus:outline-none focus:ring-1 focus:ring-brand"
            />
            <button
              type="submit"
              disabled={loading}
              className="inline-flex items-center rounded-md bg-brand text-brand-foreground px-4 py-2 text-sm font-medium hover:opacity-90 disabled:opacity-60 transition-opacity whitespace-nowrap"
            >
              {loading ? "…" : "Get early access →"}
            </button>
          </form>
        )}

        {error && <p className="mt-2 text-xs text-rose-600">{error}</p>}
      </div>

      {/* Testimonial — social proof at peak intent */}
      {testimonial && (
        <div className="border-t border-brand/20 px-5 py-4 bg-background/40">
          <blockquote className="text-sm text-muted-foreground leading-relaxed italic mb-2">
            &ldquo;{testimonial.quote}&rdquo;
          </blockquote>
          <p className="text-xs text-muted-foreground">
            {testimonial.name}
            {testimonial.role ? <span className="opacity-60"> · {testimonial.role}</span> : null}
          </p>
        </div>
      )}
    </div>
  );
}
